import { API_ENDPOINTS } from '../../config/apiEndpoints';
import apiClient from '../../utils/apiClient';
import { FrontendConfiguration, ConfigVersion, FrontendConfigurationKey } from '../../types/configuration.types';
import { RateLimitConfig, SystemConfig } from '../../types';
import { PublicSystemConfig, BackendSystemConfig } from '../../types/systemConfig.types';
import logger from '../loggerService';
import { frontendConfigurationSchema } from '../../validations/configuration.schema';
/**
 * API Service for fetching frontend configuration from backend.
 * Used to initialize the ConfigurationService with dynamic settings.
 *
 * Uses the PUBLIC endpoint /public/frontend-config for bootstrap (no auth required).
 * Uses the ADMIN endpoint /admin/configuration/frontend for write operations (auth required).
 */
class ConfigurationApi {
  private readonly baseUrl: string;

  constructor() {
    // Use window.__RUNTIME_CONFIG__ for runtime config or Vite env as fallback
    this.baseUrl =
      window.__RUNTIME_CONFIG__?.VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'http://localhost:3001/api/v1';
  }

  /**
   * Fetches frontend configuration from the backend.
   * Called once during application initialization.
   * Enforces AbortSignal-based request timeout to prevent hanging.
   *
   * @throws Error if fetch fails, times out, or response fails schema validation
   * @returns Promise<FrontendConfiguration>
   */
  async getFrontendConfig(): Promise<FrontendConfiguration> {
    const timeoutMs = Number(
      window.__RUNTIME_CONFIG__?.VITE_API_TIMEOUT_MS ||
      import.meta.env.VITE_API_TIMEOUT_MS ||
      5000
    );

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      // Use PUBLIC endpoint for bootstrap - no authentication required
      // No Content-Type header on GET — it has no body and setting 'application/json'
      // would force an unnecessary CORS preflight (application/json is not CORS-safelisted).
      // credentials: 'omit' ensures no cookies are sent, making this a true public request.
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.PUBLIC.FRONTEND_CONFIG}`, {
        method: 'GET',
        credentials: 'omit',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Validate response structure
      if (!data?.success || !data?.data) {
        throw new Error('Invalid configuration response structure');
      }

      // Pre-normalize the raw payload before strict Zod validation.
      // The backend may persist numeric values (e.g. fontSize, lineHeight) that the
      // frontend schema requires as strings. Coercing here prevents ZodError on bootstrap.
      const raw = data.data as Record<string, unknown>;
      if (raw.theme && typeof raw.theme === 'object') {
        const theme = raw.theme as Record<string, unknown>;

        // Normalize typography types
        if (theme.typography && typeof theme.typography === 'object') {
          const typo = theme.typography as Record<string, unknown>;
          if (typo.fontSize && typeof typo.fontSize === 'object') {
            const fs = typo.fontSize as Record<string, unknown>;
            typo.fontSize = Object.fromEntries(Object.entries(fs).map(([k, v]) => [k, String(v)]));
          }
          if (typo.lineHeight !== undefined) {
            typo.lineHeight = String(typo.lineHeight);
          }
          // Inject missing tracking/fontFeatureSettings defaults if absent
          if (!typo.tracking) {
            typo.tracking = { tight: '-0.025em', normal: '0em', wide: '0.025em' };
          }
          if (!typo.fontFeatureSettings) {
            typo.fontFeatureSettings = '"kern" 1, "liga" 1';
          }
        }

        // Remove empty statusColors objects from darkMode/lightMode so Zod does not
        // validate an incomplete object (undefined fields cause "Required" errors).
        const scrubStatusColors = (mode: Record<string, unknown>) => {
          if (mode.statusColors && typeof mode.statusColors === 'object') {
            const sc = mode.statusColors as Record<string, unknown>;
            const hasAll = ['success', 'warning', 'error', 'neutral', 'info'].every(k => typeof sc[k] === 'string');
            if (!hasAll) delete mode.statusColors;
          }
        };
        if (theme.darkMode && typeof theme.darkMode === 'object') scrubStatusColors(theme.darkMode as Record<string, unknown>);
        if (theme.lightMode && typeof theme.lightMode === 'object') scrubStatusColors(theme.lightMode as Record<string, unknown>);

        // Deep-merge incomplete mode-specific table objects with the global theme.table defaults.
        // The Zod schema marks theme.lightMode.table / theme.darkMode.table as optional, but when
        // the backend does send a partial object (e.g. missing headerFontSize, stripeStyle,
        // headerVariant), Zod will try to validate the incomplete shape and fail.
        // Strategy: merge global theme.table (always complete) into each mode table so all
        // required fields are guaranteed present before Zod parse.
        const REQUIRED_TABLE_FIELDS = [
          'headerFontSize',
          'stripeStyle',
          'headerVariant',
          'headerPadding',
          'cellPadding',
          'headerFontWeight',
          'headerLetterSpacing',
          'cellVerticalAlign',
          'borderStyle',
          'rowSpacing',
        ] as const;

        const globalTable =
          theme.table && typeof theme.table === 'object'
            ? (theme.table as Record<string, unknown>)
            : null;

        const ensureCompleteTable = (mode: Record<string, unknown>): void => {
          if (!mode.table || typeof mode.table !== 'object') return;
          const modeTable = mode.table as Record<string, unknown>;
          const isMissingRequired = REQUIRED_TABLE_FIELDS.some(
            (f) => modeTable[f] === undefined || modeTable[f] === null
          );
          if (isMissingRequired && globalTable) {
            // Fill every missing field from the global table defaults
            mode.table = { ...globalTable, ...modeTable };
          }
        };

        if (theme.darkMode && typeof theme.darkMode === 'object') {
          ensureCompleteTable(theme.darkMode as Record<string, unknown>);
        }
        if (theme.lightMode && typeof theme.lightMode === 'object') {
          ensureCompleteTable(theme.lightMode as Record<string, unknown>);
        }
      }

      // Strict validation of the frontend configuration payload using Zod schema to reject malformed data.
      const parsedData = frontendConfigurationSchema.parse(raw);

      return parsedData as unknown as FrontendConfiguration;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutError = new Error(`Frontend configuration fetch timed out after ${timeoutMs}ms`);
        logger.error(timeoutError.message, null, 'ConfigurationApi.getFrontendConfig');
        throw timeoutError;
      }
      logger.error('Failed to fetch frontend configuration from backend', error, 'ConfigurationApi.getFrontendConfig');
      // Re-throw to allow ConfigurationService to handle with fallbacks
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Updates a specific frontend configuration category.
   * Requires authentication (handled by apiClient interceptors if used, or manual token header).
   *
   * @param key The configuration key (e.g., 'frontend.api', 'frontend.theme')
   * @param settings The new settings object
   * @param changesSummary Optional summary of changes for audit log
   * @returns Promise<void>
   */
  async updateConfiguration<T extends object>(
    key: FrontendConfigurationKey,
    settings: T,
    changesSummary?: string,
    expectedVersion?: number,
    elevationToken?: string
  ): Promise<void> {
    try {
      const requestData: Record<string, any> = { key, settings };
      if (changesSummary !== undefined) {
        requestData.changesSummary = changesSummary;
      }
      if (expectedVersion !== undefined) {
        requestData.expectedVersion = expectedVersion;
      }

      const config: Record<string, any> = {
        method: 'PATCH',
        data: requestData,
      };

      if (elevationToken) {
        config.headers = { 'X-Elevation-Token': elevationToken };
      }

      await apiClient(API_ENDPOINTS.ADMIN.CONFIGURATION.FRONTEND, config);
    } catch (error) {
      logger.error('Failed to update frontend configuration', error, 'ConfigurationApi.updateConfiguration');
      throw error;
    }
  }

  /**
   * Fetches the current system rate limit configuration.
   * Requires 'system:rate-limits:read' permission.
   * 
   * @returns Promise<RateLimitConfig>
   */
  async getRateLimitConfig(): Promise<RateLimitConfig> {
    try {
      return await apiClient<RateLimitConfig>(API_ENDPOINTS.SYSTEM.RATE_LIMITS);
    } catch (error) {
      logger.error('Failed to fetch rate limit configuration', error, 'ConfigurationApi.getRateLimitConfig');
      throw error;
    }
  }

  /**
   * Updates the system rate limit configuration.
   * Requires 'system:rate-limits:manage' permission.
   * 
   * @param config Partial rate limit configuration to update
   * @param reason Mandatory reason for the update (audit log)
   * @param elevationToken Optional elevation token for step-up authentication
   * @returns Promise<void>
   */
  async updateRateLimitConfig(config: Partial<RateLimitConfig>, reason: string, elevationToken?: string, expectedVersion?: number): Promise<void> {
    try {
      const requestData: Record<string, any> = { config, reason };
      if (expectedVersion !== undefined) {
        requestData.expectedVersion = expectedVersion;
      }

      const clientConfig: Record<string, any> = {
        method: 'PUT',
        data: requestData,
      };

      if (elevationToken) {
        clientConfig.headers = { 'X-Elevation-Token': elevationToken };
      }

      await apiClient(API_ENDPOINTS.SYSTEM.RATE_LIMITS, clientConfig);
    } catch (error) {
      logger.error('Failed to update rate limit configuration', error, 'ConfigurationApi.updateRateLimitConfig');
      throw error;
    }
  }

  /**
   * Fetches configuration history for a specific category.
   * Requires 'configuration.frontend.read' permission.
   * 
   * @param key The configuration key (e.g., 'frontend.api', 'frontend.theme')
   * @param limit Maximum number of records to fetch
   * @returns Promise<ConfigVersion[]> Array of configuration versions
   */
  async getConfigurationHistory(key: FrontendConfigurationKey, limit: number = 10): Promise<ConfigVersion[]> {
    try {
      return await apiClient<ConfigVersion[]>(`${API_ENDPOINTS.ADMIN.CONFIGURATION.FRONTEND}/history/${key}`, {
        params: { limit }
      });
    } catch (error) {
      logger.error(`Failed to fetch configuration history for ${key}`, error, 'ConfigurationApi.getConfigurationHistory');
      throw error;
    }
  }

  /**
   * Rolls back a configuration category to a specific previous version.
   * Requires 'configuration.frontend.write' permission.
   * 
   * @param key The configuration key (e.g., 'frontend.api', 'frontend.theme')
   * @param versionId The ID of the version to rollback to
   * @returns Promise<void>
   */
  async rollbackConfiguration(key: FrontendConfigurationKey, versionId: string, elevationToken?: string): Promise<void> {
    try {
      await apiClient(`${API_ENDPOINTS.ADMIN.CONFIGURATION.FRONTEND}/rollback`, {
        method: 'PATCH',
        data: { key, versionId },
        headers: elevationToken ? { 'X-Elevation-Token': elevationToken } : {},
      });
    } catch (error) {
      logger.error(`Failed to rollback configuration for ${key}`, error, 'ConfigurationApi.rollbackConfiguration');
      throw error;
    }
  }
  /**
   * Fetches public branding config (universityName, systemName, logoUrl).
   * No authentication required — safe to call on unauthenticated pages.
   */
  async getPublicConfig(signal?: AbortSignal): Promise<PublicSystemConfig> {
    try {
      return await apiClient<PublicSystemConfig>(API_ENDPOINTS.PUBLIC.CONFIG, { signal });
    } catch (error) {
      logger.error('Failed to fetch public system config', { error }, 'ConfigurationApi.getPublicConfig');
      throw error;
    }
  }

  /**
   * Fetches the full admin system configuration.
   * Requires authentication — call only from authenticated routes.
   */
  async getAdminConfig(): Promise<BackendSystemConfig> {
    try {
      return await apiClient<BackendSystemConfig>(API_ENDPOINTS.SYSTEM.CONFIGURATION);
    } catch (error) {
      logger.error('Failed to fetch admin system config', { error }, 'ConfigurationApi.getAdminConfig');
      throw error;
    }
  }

  /**
   * Submits a PATCH update for the full system configuration with OCC support.
   *
   * @param data       The config fields to update
   * @param reason     Mandatory audit reason
   * @param expectedVersions  OCC version map for conflict detection
   */
  async updateAdminConfig(
    data: Partial<Omit<SystemConfig, 'payment'>>,
    reason: string,
    expectedVersions: Record<string, number>,
  ): Promise<{ config: BackendSystemConfig; versions?: Record<string, number> }> {
    try {
      return await apiClient<{ config: BackendSystemConfig; versions?: Record<string, number> }>(
        API_ENDPOINTS.SYSTEM.CONFIGURATION,
        { method: 'PATCH', data: { data, reason, expectedVersions } },
      );
    } catch (error) {
      logger.error('Failed to update admin system config', { error }, 'ConfigurationApi.updateAdminConfig');
      throw error;
    }
  }
}

export default new ConfigurationApi();
